import { useAuth } from "@/lib/auth";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import * as LucideIcons from "lucide-react";
import { ArrowUpRight, Circle, Pencil, Search, SquareArrowOutUpRight, PanelTopOpen, Star, Settings2, X, Check, GripVertical } from "lucide-react";
import { useNexOs } from "@/lib/nexOs";
import { useLabel, Section } from "@/lib/dynamicEngine";
import { useNdsEditor, useTileHandlers } from "@/lib/ndsEditor";
import { useOps } from "@/lib/opsCards";
import ArrangeGrid from "@/components/nds/ArrangeGrid";
import { toast } from "sonner";
import { useDemoMode } from "@/lib/demoMode";
import MoveToDialog from "@/components/nds/MoveToDialog";
import { capAction, capsForVertical, useIsSuperAdmin, usePlacedItems, useResidentActionCaps } from "@/lib/placement";
import { RESIDENT_ACTIONS_ID, folderSummary, migrateOrder } from "@/lib/residentActions";

/** localStorage keys for operator preferences persisted per-browser. */
const CUSTOM_PICKS_KEY = "nex_os_custom_picks_v1";
const CUSTOM_ORDER_KEY = "nex_os_custom_order_v1";     // full cap-id order (24 items)
const CAP_FILTER_KEY   = "nex_os_cap_filter_v1";       // last selected filter tab
const DEFAULT_CUSTOM_PICKS = ["cap_21", "cap_22", "cap_23", "cap_24"];  // seed = the 4 T6 slots

/** "clipboard-list" → ClipboardList. Fallback to Grid3x3. */
const iconFor = (name) => {
  if (!name) return LucideIcons.Grid3x3;
  const pascal = name.split("-").map((s) => s[0]?.toUpperCase() + s.slice(1)).join("");
  return LucideIcons[pascal] || LucideIcons.Grid3x3;
};

/**
 * Category → accent-color tokens. Each entry defines the top-border stripe,
 * icon tint, cursor-spotlight color and edge glow used when the tile is
 * ACTIVE. Keeping the palette in one place makes theme tweaks trivial.
 *
 * Category assignment is by cap_id so it stays stable even if labels/order
 * change through the schema editor.
 */
const CATEGORIES = {
  financial: { bar: "from-emerald-500 to-emerald-300",  icon: "text-emerald-400",  spot: "rgba(16,185,129,0.20)",  glow: "rgba(16,185,129,0.35)" },
  ops:       { bar: "from-cyan-500    to-cyan-300",     icon: "text-cyan-400",     spot: "rgba(34,211,238,0.20)",  glow: "rgba(34,211,238,0.35)" },
  data:      { bar: "from-violet-500  to-fuchsia-400",  icon: "text-violet-400",   spot: "rgba(139,92,246,0.22)",  glow: "rgba(139,92,246,0.40)" },
  comms:     { bar: "from-amber-500   to-yellow-300",   icon: "text-amber-400",    spot: "rgba(245,158,11,0.20)",  glow: "rgba(245,158,11,0.35)" },
  people:    { bar: "from-rose-500    to-pink-300",     icon: "text-rose-400",     spot: "rgba(244,63,94,0.20)",   glow: "rgba(244,63,94,0.35)" },
  content:   { bar: "from-sky-500     to-indigo-300",   icon: "text-sky-400",      spot: "rgba(56,189,248,0.20)",  glow: "rgba(56,189,248,0.35)" },
  system:    { bar: "from-brand-blue  to-brand-cyan",   icon: "text-brand-cyan",   spot: "rgba(6,182,212,0.20)",   glow: "rgba(6,182,212,0.35)" },
  custom:    { bar: "from-brand-purple to-fuchsia-500", icon: "text-brand-purple", spot: "rgba(168,85,247,0.22)",  glow: "rgba(168,85,247,0.40)" },
};

const CAP_CATEGORY = {
  cap_01: "system",   cap_02: "ops",       cap_03: "people",   cap_04: "financial",
  cap_05: "financial",cap_06: "content",   cap_07: "data",     cap_08: "ops",
  cap_09: "system",   cap_10: "people",    cap_11: "data",     cap_12: "system",
  cap_13: "comms",    cap_14: "content",   cap_15: "people",   cap_16: "data",
  cap_17: "financial",cap_18: "financial", cap_19: "financial",cap_20: "system",
  cap_21: "custom",   cap_22: "custom",    cap_23: "custom",   cap_24: "custom",
  cap_25: "financial", cap_p1: "people", cap_p2: "ops", cap_p3: "content", cap_p4: "comms", cap_fin_res_actions: "financial", cap_fin_01: "financial", cap_fin_02: "financial", cap_fin_03: "financial", cap_fin_04: "financial", cap_fin_05: "financial", cap_fin_06: "financial", cap_fin_07: "financial", cap_fin_08: "financial", cap_fin_09: "financial", cap_fin_10: "financial", cap_fin_11: "financial", cap_fin_12: "financial", cap_fin_13: "financial", cap_fin_14: "financial", cap_fin_15: "financial", cap_fin_16: "financial", cap_fin_17: "financial", cap_par_01: "people", cap_re_intake: "people", cap_re_portal_as: "content", cap_re_checkin: "people", cap_re_docs: "people", cap_re_sa: "system", cap_re_properties: "people", cap_re_room_assignments: "people", cap_re_incidents: "people", cap_par_02: "people", cap_par_03: "people", cap_par_04: "financial", cap_par_05: "system", cap_doc_01: "content", cap_doc_02: "content", cap_doc_03: "comms", cap_doc_04: "comms", cap_doc_05: "data", cap_sch_01: "ops", cap_sch_02: "ops", cap_sch_03: "data",
};

/** Flat dollar price per tier. The row chip surfaces this so the client
 *  reads pricing at a glance without hunting each per-tile credit badge. */
const TIER_PRICE = { 1: 500, 2: 500, 3: 750, 4: 750, 5: 1000, 6: 1500 };

/** Left-chip color tokens. Mirrors the per-tile TierBadge palette and adds
 *  a violet stop for T6 Custom. Uses gradient for the vertical rail. */
const TIER_CHIP = {
  1: { from: "from-emerald-500", to: "to-emerald-300", text: "text-emerald-950",  ring: "ring-emerald-500/40" },
  2: { from: "from-sky-500",     to: "to-sky-300",     text: "text-sky-950",      ring: "ring-sky-500/40"     },
  3: { from: "from-amber-500",   to: "to-amber-300",   text: "text-amber-950",    ring: "ring-amber-500/40"   },
  4: { from: "from-orange-500",  to: "to-orange-300",  text: "text-orange-950",   ring: "ring-orange-500/40"  },
  5: { from: "from-red-500",     to: "to-red-300",     text: "text-red-50",       ring: "ring-red-500/40"     },
  6: { from: "from-nex-violet",  to: "to-nex-gold",    text: "text-white",        ring: "ring-nex-violet/50"  },
};

/**
 * Tier 3 — 20-Capability Roadmap.
 *
 * Visual upgrade:
 *   • Category-colored top border stripe + icon tint
 *   • Glowing ACTIVE pill (pulsing green dot) or muted STANDBY pill
 *   • Live per-tile metric line pulled from /api/nex-os/capability-metrics
 *   • Radial cursor spotlight that tracks the mouse
 *   • Soft hover lift with an ambient category-tinted shadow
 *
 * Interaction: left-click fires the functional action popup; right-click
 * (or long-press) opens the Quick-Edit schema window. Unchanged.
 */
// Owner card config (label/description/status/link) layered over the template tile.
const effCap = (cap, cfg) => (cfg ? {
  ...cap, label: cfg.label || cap.label, description: cfg.description || cap.description,
  status: cfg.status || cap.status, href: cfg.link_href || cap.href,
} : cap);
const counterText = (cfg, ops) => {
  if (cfg?.counter === "credit_balance" && ops.balance != null) return `${Math.round(ops.balance).toLocaleString()} credits left`;
  if (cfg?.counter === "entries_week" && cfg.counter_form) return `${ops.counters[cfg.counter_form] || 0} entries this week`;
  return null;
};

export default function CapabilityRoadmap() {
  const partnerDesk = useAuth().user?.role === "partner_user";
  const { currentVertical, capMetrics: rawMetrics } = useNexOs();
  const raKids = useResidentActionCaps();
  const raKey = raKids.map((c) => c.id).join(",");
  const capMetrics = useMemo(() => (rawMetrics?.[RESIDENT_ACTIONS_ID]
    ? { ...rawMetrics, [RESIDENT_ACTIONS_ID]: { ...rawMetrics[RESIDENT_ACTIONS_ID], metric: folderSummary(rawMetrics, raKey.split(",")) } } : rawMetrics), [rawMetrics, raKey]);
  const { fireTileAction, openTileEditor } = useNdsEditor();
  const ops = useOps();
  const demoMode = useDemoMode();
  const kicker = useLabel("nds.caps.kicker", "// FULL PLATFORM ROADMAP · 20 CAPABILITIES");
  const heading = useLabel("nds.caps.heading", "Modular capabilities. One universal grid.");
  const subheading = useLabel(
    "nds.caps.subheading",
    "The 10 steps above are the sequence — these 20 are every operational surface we deploy for your business.",
  );
  const placed = usePlacedItems();
  const isSA = useIsSuperAdmin();
  const [moving, setMoving] = useState(null);
  const vis = useAuth().user?.visible_cards;
  const caps = useMemo(() => (demoMode || !ops.ready || !currentVertical
    ? (currentVertical?.capabilities || []).filter((c) => isSA || !Array.isArray(vis) || vis.includes(c.id))
    : capsForVertical(placed, currentVertical.id)), [demoMode, ops.ready, currentVertical, placed, isSA, vis]);
  // A card placed after the user's last saved arrangement drops to the bottom.
  const freshPlaced = (c) => c.placed_at && (!ops.layout.updated_at || c.placed_at > ops.layout.updated_at);

  // Search + category filter. Groupings mirror the /demo prospect surface;
  // `custom` here is a user-curated pin list persisted in localStorage rather
  // than the T6 tier bucket (T6 cards now live inside `all` alongside the rest).
  const [search, setSearch] = useState("");
  const [group, setGroup]   = useState(() => {
    try {
      const raw = localStorage.getItem(CAP_FILTER_KEY);
      if (raw && ["all", "financial", "ops", "content", "system", "custom"].includes(raw)) return raw;
    } catch { /* first-run / SSR */ }
    return "all";
  });
  const [customPicks, setCustomPicks] = useState(() => {
    try {
      const raw = localStorage.getItem(CUSTOM_PICKS_KEY);
      if (raw) {
        const arr = JSON.parse(raw);
        if (Array.isArray(arr)) return arr;
      }
    } catch { /* first-run / SSR — fall back to defaults */ }
    return DEFAULT_CUSTOM_PICKS;
  });
  const [customOrder, setCustomOrder] = useState(() => {
    try {
      const raw = localStorage.getItem(CUSTOM_ORDER_KEY);
      if (raw) {
        const arr = JSON.parse(raw);
        if (Array.isArray(arr)) return arr;
      }
    } catch { /* first-run — will inherit schema order */ }
    return null;
  });
  const [pickerOpen, setPickerOpen] = useState(false);

  useEffect(() => {
    try { localStorage.setItem(CUSTOM_PICKS_KEY, JSON.stringify(customPicks)); } catch { /* quota / private mode */ }
  }, [customPicks]);
  useEffect(() => {
    try {
      if (customOrder && customOrder.length) localStorage.setItem(CUSTOM_ORDER_KEY, JSON.stringify(customOrder));
    } catch { /* quota / private mode */ }
  }, [customOrder]);
  useEffect(() => {
    try { localStorage.setItem(CAP_FILTER_KEY, group); } catch { /* quota */ }
  }, [group]);
  // Each engine tab has its own small card set — a remembered category filter must not blank it.
  const verticalId = currentVertical?.id;
  const firstVertical = useRef(true);
  useEffect(() => {
    if (firstVertical.current) { firstVertical.current = false; return; }
    setGroup("all");
  }, [verticalId]);

  const customSet = useMemo(() => new Set(migrateOrder(customPicks)), [customPicks]);
  const GROUPS = useMemo(() => ({
    all:       null,
    financial: new Set(["financial"]),
    ops:       new Set(["ops", "people"]),
    content:   new Set(["content", "data", "comms"]),
    system:    new Set(["system"]),
    // `custom` is intentionally left null — it's handled by cap-id membership below.
    custom:    null,
  }), []);

  // Arrange mode draft: { order, hidden } — saved per user on Done.
  const [arr, setArr] = useState(null);
  const hiddenSet = useMemo(() => new Set(arr?.hidden || ops.layout.hidden || []), [arr, ops.layout]);
  const masterOrder = () => {
    const src = migrateOrder(ops.layout.order.length ? ops.layout.order : (customOrder || []));
    const rank = new Map(src.map((id, i) => [id, i]));
    caps.filter(freshPlaced).forEach((c) => rank.delete(c.id));
    return caps.map((c) => c.id).sort((a, b) => (rank.has(a) ? rank.get(a) : 1e9) - (rank.has(b) ? rank.get(b) : 1e9));
  };
  const startArrange = () => setArr({ order: masterOrder(), hidden: [...(ops.layout.hidden || [])] });
  const moveCard = (from, to) => setArr((d) => {
    const o = [...d.order]; const i = o.indexOf(from); const j = o.indexOf(to);
    o.splice(i, 1); o.splice(j, 0, from); return { ...d, order: o };
  });
  const finishArrange = async () => {
    try { await ops.saveLayout(arr); toast.success("Board layout saved"); setArr(null); }
    catch { toast.error("Could not save layout"); }
  };

  const filteredCaps = useMemo(() => {
    const q = search.trim().toLowerCase();
    // First filter down to the tab's set + search hits...
    const passing = caps.filter((c) => {
      if (hiddenSet.has(c.id)) return false;
      let okGroup;
      if (group === "all") {
        okGroup = true;
      } else if (group === "custom") {
        okGroup = customSet.has(c.id);
      } else {
        const bucket = GROUPS[group];
        okGroup = bucket ? bucket.has(CAP_CATEGORY[c.id] || "system") : true;
      }
      if (!okGroup) return false;
      if (!q) return true;
      return (c.label || "").toLowerCase().includes(q)
          || (c.description || "").toLowerCase().includes(q)
          || (c.status || "").toLowerCase().includes(q);
    });
    // ...then, if the operator has drag-reordered the picker list, respect
    // that order. Ids missing from the saved order (e.g. new caps added by
    // an admin after the reorder) fall to the bottom in schema order.
    const orderSrc = migrateOrder(arr?.order || (ops.layout.order.length ? ops.layout.order : customOrder));
    if (!orderSrc || !orderSrc.length) return passing;
    const rank = new Map(orderSrc.map((id, i) => [id, i]));
    if (!arr) caps.filter(freshPlaced).forEach((c) => rank.delete(c.id));
    return [...passing].sort((a, b) => {
      const ra = rank.has(a.id) ? rank.get(a.id) : Number.MAX_SAFE_INTEGER;
      const rb = rank.has(b.id) ? rank.get(b.id) : Number.MAX_SAFE_INTEGER;
      return ra - rb;
    });
  }, [caps, search, group, GROUPS, customSet, customOrder, arr, ops.layout, hiddenSet]);

  const groupCounts = useMemo(() => {
    const counts = { all: caps.length, custom: caps.filter((c) => customSet.has(c.id)).length };
    for (const [k, set] of Object.entries(GROUPS)) {
      if (k === "all" || k === "custom") continue;
      counts[k] = caps.filter((c) => set.has(CAP_CATEGORY[c.id] || "system")).length;
    }
    return counts;
  }, [caps, GROUPS, customSet]);
  // Saved category filter that matches nothing in this tab → fall back to All.
  useEffect(() => {
    if (caps.length && group !== "all" && group !== "custom" && !groupCounts[group]) setGroup("all");
  }, [caps.length, group, groupCounts]);

  const renderTile = (cap) => {
          const target = currentVertical ? { kind: "capability", verticalId: currentVertical.id, itemId: cap.id } : null;
          const category = CATEGORIES[CAP_CATEGORY[cap.id] || "system"];
          const metric = capMetrics?.[cap.id];
          const isLive = metric?.is_live !== undefined ? metric.is_live : cap.tone === "live";
          const pinned = customSet.has(cap.id);
          return (
            <Section scope="nds_capability" id={cap.id} key={cap.id}>
              <CapabilityTile
                cap={effCap(cap, ops.cards[cap.id])}
                linkTarget={ops.cards[cap.id]?.link_target}
                counterText={counterText(ops.cards[cap.id], ops)}
                onConfigure={ops.isOwner ? () => fireTileAction({ kind: "cardConfig", title: `Configure · ${effCap(cap, ops.cards[cap.id]).label}`, cap }) : null}
                target={target}
                category={category}
                metric={metric}
                isLive={isLive}
                pinned={pinned}
                onTogglePin={() => setCustomPicks((prev) => prev.includes(cap.id) ? prev.filter((x) => x !== cap.id) : [...prev, cap.id])}
                onAction={() => fireTileAction(capAction(cap, ops.cards[cap.id]))}
                onEdit={openTileEditor}
              />
            </Section>
          );
  };
  const capById = Object.fromEntries(caps.map((c) => [c.id, c]));
  const removed = arr ? arr.hidden.map((id) => capById[id]).filter(Boolean) : [];

  return (
    <section data-testid="nds-capabilities" className="flex flex-col gap-5">
      <div className="flex flex-col gap-2">
        <div className="nds-label-dim">{kicker}</div>
        <h2 className="text-slate-100 text-2xl md:text-3xl font-serif tracking-tight">
          {heading}
        </h2>
        <p className="text-slate-400 text-sm max-w-3xl">{subheading}</p>
      </div>

      {/* Search + category filter — same pattern as the /demo prospect view. */}
      <CapFilterBar
        search={search} setSearch={setSearch}
        group={group}   setGroup={setGroup}
        counts={groupCounts}
        onManageCustom={() => setPickerOpen(true)}
      />

      {/* Tier pricing legend — a single compact row so operators still see
          the T1-T6 pricing hierarchy after we flattened the tier rows. */}
      {!partnerDesk && <TierPriceLegend />}

      {!demoMode && !partnerDesk && ops.ready && (
        <div data-testid="nds-arrange-bar" className="flex flex-wrap items-center gap-2">
          {!arr ? (
            <button type="button" onClick={startArrange} data-testid="nds-arrange-toggle"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-slate-700 text-slate-300 hover:text-brand-cyan hover:border-brand-cyan/60 font-mono text-[0.6rem] tracking-[0.16em] uppercase transition-colors">
              <LucideIcons.LayoutGrid className="w-3.5 h-3.5" /> Arrange cards
            </button>
          ) : (
            <>
              <span className="font-mono text-[0.6rem] tracking-[0.16em] uppercase text-brand-cyan mr-1">Drag cards to move · ✕ removes</span>
              <button type="button" onClick={finishArrange} data-testid="nds-arrange-done"
                className="px-3 py-1.5 rounded-md bg-gradient-to-r from-brand-blue to-brand-cyan text-navy-950 font-mono text-[0.6rem] font-bold tracking-[0.16em] uppercase hover:brightness-110">Done</button>
              <button type="button" onClick={() => setArr(null)} data-testid="nds-arrange-cancel"
                className="px-3 py-1.5 rounded-md border border-slate-700 text-slate-300 hover:text-white font-mono text-[0.6rem] tracking-[0.16em] uppercase">Cancel</button>
              <button type="button" onClick={() => setArr((d) => ({ ...d, order: caps.map((c) => c.id) }))} data-testid="nds-arrange-reset"
                className="px-3 py-1.5 rounded-md border border-slate-700 text-slate-400 hover:text-white font-mono text-[0.6rem] tracking-[0.16em] uppercase">Reset order</button>
              {removed.length > 0 && (
                <div data-testid="nds-arrange-removed" className="flex flex-wrap items-center gap-1.5 ml-2">
                  <span className="font-mono text-[0.6rem] tracking-[0.16em] uppercase text-slate-500">Removed:</span>
                  {removed.map((c) => (
                    <button key={c.id} type="button" onClick={() => setArr((d) => ({ ...d, hidden: d.hidden.filter((x) => x !== c.id) }))}
                      data-testid={`nds-arrange-restore-${c.id}`} title="Put back on the board"
                      className="px-2 py-1 rounded-full border border-slate-700 text-[0.65rem] text-slate-300 hover:text-brand-cyan hover:border-brand-cyan/60">+ {c.label}</button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Flat 4-across grid regardless of filter. On mobile it steps down to
          1/2/3 cols, but every tab lands at 4 across on desktop as requested. */}
      {arr && (
        <ArrangeGrid ids={filteredCaps.map((c) => c.id)} renderTile={(id) => renderTile(capById[id])}
          onMove={moveCard} onRemove={(id) => setArr((d) => ({ ...d, hidden: [...d.hidden, id] }))}
          onMoveTo={isSA ? (id) => setMoving(capById[id]) : undefined} />
      )}
      {moving && <MoveToDialog item={moving} onClose={() => setMoving(null)} />}
      <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 ${arr ? "hidden" : ""}`} data-testid="nds-cap-grid">
        {!arr && filteredCaps.map((cap) => renderTile(cap))}
        {caps.length === 0 && (
          <div className="col-span-full nds-card p-8 text-center font-mono text-sm text-slate-300" data-testid="nds-caps-empty">
            Loading capabilities…
          </div>
        )}
        {caps.length > 0 && filteredCaps.length === 0 && (
          <div className="col-span-full nds-card p-8 text-center font-mono text-sm text-slate-400" data-testid="nds-caps-noresult">
            {group === "custom"
              ? "No cards pinned yet — click Manage Picks above to build your Custom set."
              : "No capabilities match this filter."}
          </div>
        )}
      </div>

      {pickerOpen && (
        <CustomPicksModal
          caps={caps}
          picks={customPicks}
          order={customOrder}
          onSave={(ids, orderedIds) => { setCustomPicks(ids); setCustomOrder(orderedIds); setPickerOpen(false); }}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </section>
  );
}

/** Compact single-row legend so operators still see tier pricing after the
 *  per-tier row chips were removed. */
function TierPriceLegend() {
  const stops = [
    { tiers: "T1 · T2", price: "$500",   from: "from-emerald-500", to: "to-sky-500" },
    { tiers: "T3 · T4", price: "$750",   from: "from-amber-500",   to: "to-orange-500" },
    { tiers: "T5",      price: "$1,000", from: "from-red-500",     to: "to-red-400" },
    { tiers: "T6",      price: "$1,500", from: "from-brand-purple",to: "to-fuchsia-500" },
  ];
  return (
    <div
      className="flex flex-wrap items-center gap-2 px-3 py-2 rounded-lg bg-navy-900/60 border border-slate-800 font-mono text-[0.65rem] tracking-[0.14em] uppercase"
      data-testid="nds-tier-legend"
    >
      <span className="text-slate-500 pr-1">Tier pricing</span>
      {stops.map((s) => (
        <span key={s.tiers} className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-gradient-to-r ${s.from} ${s.to} text-white/95 shadow-sm`}>
          <span className="font-bold">{s.price}</span>
          <span className="opacity-80">{s.tiers}</span>
        </span>
      ))}
    </div>
  );
}

/** Modal for hand-picking + reordering which caps land in the "Custom" tab.
 *  Native HTML5 drag-and-drop — no external DnD library. Persists to
 *  localStorage on save (no backend round-trip). */
function CustomPicksModal({ caps, picks, order, onSave, onClose }) {
  const [draftPicks, setDraftPicks] = useState(() => new Set(picks));
  // Draft order = saved order (if any), then any caps missing from it in
  // schema order. Guarantees every cap appears exactly once even after
  // the platform adds a new capability.
  const [draftOrder, setDraftOrder] = useState(() => {
    const saved = Array.isArray(order) ? order : [];
    const seen  = new Set(saved.filter((id) => caps.some((c) => c.id === id)));
    const tail  = caps.filter((c) => !seen.has(c.id)).map((c) => c.id);
    return [...saved.filter((id) => caps.some((c) => c.id === id)), ...tail];
  });
  const [dragId, setDragId]   = useState(null);
  const [overId, setOverId]   = useState(null);

  const byId = useMemo(() => new Map(caps.map((c) => [c.id, c])), [caps]);

  const toggle = (id) => {
    setDraftPicks((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const move = (fromId, toId) => {
    if (!fromId || !toId || fromId === toId) return;
    setDraftOrder((prev) => {
      const next = [...prev];
      const from = next.indexOf(fromId);
      const to   = next.indexOf(toId);
      if (from < 0 || to < 0) return prev;
      next.splice(from, 1);
      next.splice(to, 0, fromId);
      return next;
    });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-navy-950/80 backdrop-blur-md"
      onClick={onClose}
      data-testid="nds-custom-picker"
    >
      <div
        className="w-full max-w-2xl max-h-[85vh] flex flex-col rounded-2xl border border-slate-700 bg-navy-900/95 backdrop-blur-lg shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-800">
          <div>
            <div className="text-[10px] font-mono tracking-[0.18em] uppercase text-brand-cyan">// custom tab</div>
            <h3 className="text-lg font-bold text-white">Pick + drag-reorder your Custom set</h3>
            <p className="text-xs text-slate-400 mt-0.5">
              {draftPicks.size} of {caps.length} pinned · drag any row to reorder · saved to this browser
            </p>
          </div>
          <button onClick={onClose} data-testid="nds-custom-picker-close" className="w-8 h-8 rounded-lg bg-navy-850 border border-slate-700 text-slate-400 hover:text-white flex items-center justify-center transition">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="overflow-y-auto p-4 flex flex-col gap-1.5" data-testid="nds-custom-pick-list">
          {draftOrder.map((id) => {
            const c = byId.get(id);
            if (!c) return null;
            const on   = draftPicks.has(id);
            const over = overId === id && dragId && dragId !== id;
            return (
              <div
                key={id}
                data-testid={`nds-custom-pick-${id}`}
                draggable
                onDragStart={() => setDragId(id)}
                onDragOver={(e) => { e.preventDefault(); setOverId(id); }}
                onDragLeave={() => { if (overId === id) setOverId(null); }}
                onDrop={(e) => { e.preventDefault(); move(dragId, id); setDragId(null); setOverId(null); }}
                onDragEnd={() => { setDragId(null); setOverId(null); }}
                className={`flex items-center gap-2 px-2.5 py-2 rounded-lg border transition select-none cursor-grab active:cursor-grabbing ${
                  on ? "border-brand-cyan/60 bg-brand-cyan/10" : "border-slate-800 bg-navy-950 hover:border-slate-700"
                } ${over ? "ring-2 ring-brand-cyan/60 -translate-y-0.5" : ""} ${dragId === id ? "opacity-40" : ""}`}
              >
                <span
                  className="w-4 h-6 grid place-items-center text-slate-500 hover:text-slate-300 shrink-0"
                  title="Drag to reorder"
                  aria-hidden="true"
                >
                  <GripVertical className="w-4 h-4" />
                </span>
                <button
                  type="button"
                  onClick={() => toggle(id)}
                  data-testid={`nds-custom-pick-toggle-${id}`}
                  className={`w-4 h-4 shrink-0 rounded border flex items-center justify-center transition ${
                    on ? "border-brand-cyan bg-brand-cyan text-navy-950" : "border-slate-600 hover:border-slate-400"
                  }`}
                  aria-label={on ? "Unpin" : "Pin"}
                >
                  {on && <Check className="w-3 h-3" strokeWidth={3} />}
                </button>
                <span className="flex-1 min-w-0 pl-1" onClick={() => toggle(id)}>
                  <span className="block text-sm text-slate-100 font-semibold truncate">{c.label}</span>
                  <span className="block text-[10px] font-mono tracking-[0.14em] uppercase text-slate-500 mt-0.5">
                    T{c.build_tier || 3} · {(c.status || "").slice(0, 32)}
                  </span>
                </span>
              </div>
            );
          })}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-800 bg-navy-950">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg bg-navy-850 hover:bg-navy-800 text-slate-300 border border-slate-700 text-xs font-medium transition"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onSave([...draftPicks], [...draftOrder])}
            data-testid="nds-custom-picks-save"
            className="px-4 py-2 rounded-lg bg-gradient-to-r from-brand-blue to-brand-cyan text-white text-xs font-semibold shadow-lg shadow-cyan-500/20 transition"
          >
            Save {draftPicks.size} pick{draftPicks.size === 1 ? "" : "s"}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Sticky search + category filter row. Mirrors the /demo prospect UI so
 *  the operator sees the same controls prospects do. */
function CapFilterBar({ search, setSearch, group, setGroup, counts, onManageCustom }) {
  const groups = [
    { key: "all",       label: `All (${counts.all ?? 0})` },
    { key: "financial", label: `Financial (${counts.financial ?? 0})` },
    { key: "ops",       label: `Ops & People (${counts.ops ?? 0})` },
    { key: "content",   label: `Content · Data · Comms (${counts.content ?? 0})` },
    { key: "system",    label: `System (${counts.system ?? 0})` },
    { key: "custom",    label: `Custom (${counts.custom ?? 0})` },
  ];
  return (
    <div
      className="flex flex-col md:flex-row gap-3 items-stretch md:items-center justify-between bg-navy-900/60 p-3 rounded-xl border border-slate-800"
      data-testid="nds-cap-filterbar"
    >
      <div className="relative w-full md:w-72 shrink-0">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden="true" />
        <input
          data-testid="nds-cap-search"
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search capabilities…"
          className="w-full bg-navy-950 border border-slate-700/80 rounded-lg pl-9 pr-3 py-2 text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:border-brand-blue transition"
        />
      </div>
      <div className="flex flex-wrap items-center gap-2 flex-1 md:justify-end">
        {groups.map((g) => (
          <button
            key={g.key}
            type="button"
            data-testid={`nds-cap-filter-${g.key}`}
            onClick={() => setGroup(g.key)}
            className={
              group === g.key
                ? "text-xs px-3 py-1.5 rounded-lg border border-brand-blue bg-brand-blue/20 text-brand-cyan font-medium transition"
                : "text-xs px-3 py-1.5 rounded-lg border border-slate-800 bg-navy-950 text-slate-400 hover:text-slate-200 transition"
            }
          >
            {g.label}
          </button>
        ))}
        {group === "custom" && (
          <button
            type="button"
            onClick={onManageCustom}
            data-testid="nds-cap-manage-picks"
            className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-brand-cyan/40 bg-brand-cyan/10 text-brand-cyan hover:bg-brand-cyan/20 font-medium transition"
          >
            <Settings2 className="w-3.5 h-3.5" /> Manage Picks
          </button>
        )}
      </div>
    </div>
  );
}

/** One tier row = small price pill header + a 4-col grid of cards below. */
function TierRow({ tier, caps, currentVertical, capMetrics, fireTileAction, openTileEditor }) {
  const partnerDesk = useAuth().user?.role === "partner_user";
  const chip = TIER_CHIP[tier] || TIER_CHIP[3];
  const price = TIER_PRICE[tier];
  const demoMode = useDemoMode();
  return (
    <div className="flex flex-col gap-2" data-testid={`nds-cap-tier-row-${tier}`}>
      {!demoMode && !partnerDesk && <TierPriceChip tier={tier} price={price} chip={chip} />}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
        {caps.map((cap) => {
          const target = currentVertical ? { kind: "capability", verticalId: currentVertical.id, itemId: cap.id } : null;
          const category = CATEGORIES[CAP_CATEGORY[cap.id] || "system"];
          const metric = capMetrics?.[cap.id];
          const isLive = metric?.is_live !== undefined ? metric.is_live : cap.tone === "live";
          return (
            <Section scope="nds_capability" id={cap.id} key={cap.id}>
              <CapabilityTile
                cap={cap}
                target={target}
                category={category}
                metric={metric}
                isLive={isLive}
                onAction={() => fireTileAction({
                  kind:        cap.modal_kind || "open_module",
                  title:       cap.label,
                  description: cap.description,
                  href:        cap.href,
                })}
                onEdit={openTileEditor}
              />
            </Section>
          );
        })}
      </div>
    </div>
  );
}

/** Small price-only pill anchored to the left of each tier row. Gradient
 *  matches the tier accent so it reads as the row header without stealing
 *  focus from the cards themselves. */
function TierPriceChip({ tier, price, chip }) {
  return (
    <div
      className={`shrink-0 self-start rounded-full ring-1 ${chip.ring} bg-gradient-to-r ${chip.from} ${chip.to} ${chip.text} shadow-sm px-3 py-1 font-mono text-[0.75rem] font-bold tracking-tight leading-none whitespace-nowrap`}
      data-testid={`nds-tier-chip-${tier}`}
      title={`Tier ${tier} — $${price?.toLocaleString?.() || price} per capability`}
    >
      ${price?.toLocaleString?.() || price}
    </div>
  );
}

/** A desk card rendered elsewhere (e.g. inside the Resident Actions folder): same tile, live badge, Pop and Full. */
export function DeskCapTile({ cap }) {
  const { capMetrics } = useNexOs();
  const { fireTileAction, openTileEditor } = useNdsEditor();
  const metric = capMetrics?.[cap.id];
  return (
    <CapabilityTile cap={cap} target={null} category={CATEGORIES[CAP_CATEGORY[cap.id] || "system"]} metric={metric}
      isLive={metric?.is_live !== undefined ? metric.is_live : cap.tone === "live"} onEdit={openTileEditor}
      onAction={() => fireTileAction({ kind: cap.modal_kind || "open_module", title: cap.label, description: cap.description, href: cap.href })} />
  );
}

function CapabilityTile({ cap, target, category, metric, isLive, pinned, onTogglePin, onAction, onEdit, onConfigure, counterText, linkTarget }) {
  const isPartner = useAuth().user?.role === "partner_user";
  const Icon = iconFor(cap.icon);
  const demoMode = useDemoMode();
  const tileHandlers = useTileHandlers({ target, onEdit });
  const ref = useRef(null);

  // Radial cursor spotlight — updates two CSS vars so the ::before mask
  // tracks the mouse without a re-render on every mousemove.
  const onMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    el.style.setProperty("--spot-x", `${e.clientX - r.left}px`);
    el.style.setProperty("--spot-y", `${e.clientY - r.top}px`);
  };

  return (
    <div
      ref={ref}
      onMouseMove={onMove}
      {...tileHandlers}
      data-testid={`nds-cap-${cap.id}`}
      data-live={isLive ? "true" : "false"}
      className="nds-cap-tile group relative pt-4 pb-3 px-4 flex flex-col gap-2 transition-all duration-300 hover:-translate-y-0.5"
      style={{
        "--spot-color": category.spot,
        "--glow-color": isLive ? category.glow : "rgba(148, 163, 184, 0.12)",
      }}
      title="Right-click to edit"
    >
      {/* Category accent bar */}
      <span className={`absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r ${category.bar}`} />

      {/* Row: icon + tier badge + status pill */}
      <div className="flex items-start justify-between gap-2 relative z-[1]">
        <div className={`w-8 h-8 grid place-items-center rounded-lg border border-slate-800 bg-navy-950 ${category.icon}`}>
          <Icon className="w-3.5 h-3.5" strokeWidth={1.8} />
        </div>
        <div className="flex items-center gap-1.5">
          <TierBadge tier={cap.build_tier} credits={demoMode ? null : cap.est_credits} />
          <StatusPill isLive={isLive} tone={cap.tone} />
        </div>
      </div>

      {/* Title + pin star + pencil edit icon */}
      <div className="flex items-start justify-between gap-2 relative z-[1]">
        <div className="font-serif text-[0.95rem] font-bold text-slate-100 leading-tight flex-1 group-hover:text-brand-cyan transition-colors">
          {cap.label}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onTogglePin?.(); }}
            data-testid={`nds-cap-pin-${cap.id}`}
            title={pinned ? "Remove from Custom tab" : "Pin to Custom tab"}
            className={`w-6 h-6 grid place-items-center rounded-md border transition-all ${
              pinned
                ? "border-brand-cyan/60 bg-brand-cyan/10 text-brand-cyan opacity-100"
                : "border-slate-700 bg-navy-850 text-slate-400 opacity-0 group-hover:opacity-100 hover:text-brand-cyan hover:border-brand-cyan/60"
            }`}
          >
            <Star className="w-3 h-3" strokeWidth={2} fill={pinned ? "currentColor" : "none"} />
          </button>
          {onConfigure && !isPartner && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onConfigure(); }}
              data-testid={`nds-cap-configure-${cap.id}`}
              title="Configure card — link, quick forms, counter"
              className="opacity-0 group-hover:opacity-100 w-6 h-6 grid place-items-center rounded-md border border-slate-700 bg-navy-850 text-slate-400 hover:text-brand-cyan hover:border-brand-cyan/60 transition-all shrink-0"
            >
              <Settings2 className="w-3 h-3" strokeWidth={2} />
            </button>
          )}
          {!isPartner && <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onEdit?.(target); }}
            data-testid={`nds-cap-pencil-${cap.id}`}
            title="Edit label"
            className="opacity-0 group-hover:opacity-100 w-6 h-6 grid place-items-center rounded-md border border-slate-700 bg-navy-850 text-slate-400 hover:text-brand-cyan hover:border-brand-cyan/60 transition-all shrink-0"
          >
            <Pencil className="w-3 h-3" strokeWidth={2} />
          </button>}
        </div>
      </div>

      {/* Description */}
      {cap.description && (
        <p className="text-[0.75rem] text-slate-400 leading-snug min-h-[2.4rem] relative z-[1]">
          {cap.description}
        </p>
      )}

      {/* Live metric — sits on its own row so the two action buttons below
          have full width to breathe. */}
      <div className="pt-1.5 border-t border-slate-800/80 relative z-[1]">
        <span
          className="font-mono text-[0.6rem] tracking-[0.14em] uppercase text-slate-500 truncate block"
          data-testid={`nds-cap-metric-${cap.id}`}
        >
          {metric?.metric || cap.status || "READY"}
        </span>
        {counterText && (
          <span data-testid={`nds-cap-counter-${cap.id}`} className="mt-1 block font-mono text-[0.62rem] tracking-[0.12em] uppercase text-brand-cyan truncate">
            {counterText}
          </span>
        )}
      </div>

      {/* Side-by-side action row.
       *  POP    — opens the floating popup (Brief Intake / New Contact / etc.).
       *  FULL   — deep-links to the dedicated admin module page.
       *  Cards without a `href` (queued/T6 slots) render FULL disabled with a
       *  "Coming Soon" hint so the layout stays consistent. */}
      <div className="grid grid-cols-2 gap-1.5 relative z-[1]" data-testid={`nds-cap-actions-${cap.id}`}>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onAction(); }}
          data-testid={`nds-cap-pop-${cap.id}`}
          className="inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded-md border border-brand-blue/40 bg-brand-blue/10 hover:bg-brand-blue/20 text-brand-cyan font-mono text-[0.6rem] tracking-[0.14em] uppercase transition"
          title="Open action popup"
        >
          <PanelTopOpen className="w-3 h-3" /> Pop
        </button>
        {cap.href ? (
          <a
            href={cap.href}
            target={linkTarget === "same" ? undefined : "_blank"}
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            data-testid={`nds-cap-full-${cap.id}`}
            className="inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded-md border border-brand-emerald/40 bg-brand-emerald/10 hover:bg-brand-emerald/20 text-emerald-400 font-mono text-[0.6rem] tracking-[0.14em] uppercase transition no-underline"
            title="Open full module page in a new tab"
          >
            <SquareArrowOutUpRight className="w-3 h-3" /> Full
          </a>
        ) : (
          <span
            data-testid={`nds-cap-full-disabled-${cap.id}`}
            className="inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded-md border border-slate-800 bg-navy-950 text-slate-600 font-mono text-[0.6rem] tracking-[0.14em] uppercase cursor-not-allowed"
            title="No standalone module yet"
          >
            <SquareArrowOutUpRight className="w-3 h-3" /> Soon
          </span>
        )}
      </div>
    </div>
  );
}

/** Small pastel pill showing build tier T1-T5 + estimated credit cost.
 *  Colors go emerald (cheap) → red (expensive) so operators can pattern-match
 *  pricing at a glance without reading the number. */
function TierBadge({ tier, credits }) {
  if (!tier) return null;
  const styles = {
    1: "border-emerald-500/40 bg-emerald-500/10 text-emerald-400",
    2: "border-sky-500/40    bg-sky-500/10    text-sky-400",
    3: "border-amber-500/40  bg-amber-500/10  text-amber-400",
    4: "border-orange-500/40 bg-orange-500/10 text-orange-400",
    5: "border-red-500/40    bg-red-500/10    text-red-400",
    6: "border-brand-purple/40 bg-brand-purple/10 text-brand-purple",
  };
  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-[3px] border font-mono ${styles[tier] || styles[3]}`}
      style={{ fontSize: "0.55rem" }}
      data-testid={`nds-cap-tier`}
      title={`Build tier ${tier} · est. ${credits} credits`}
    >
      <span className="font-bold tracking-[0.14em] uppercase">T{tier}</span>
      {credits != null && <span className="tracking-[0.14em]">{credits}cr</span>}
    </span>
  );
}

function StatusPill({ isLive, tone }) {
  if (isLive) {
    return (
      <span
        className="inline-flex items-center gap-1.5 px-2 py-[3px] border border-emerald-500/40 bg-emerald-500/10 text-emerald-700"
        data-testid="nds-cap-status-live"
        style={{ fontSize: "0.55rem" }}
      >
        <span className="relative flex w-1.5 h-1.5">
          <span className="absolute inset-0 rounded-full bg-emerald-400 animate-ping opacity-75" />
          <span className="relative rounded-full w-1.5 h-1.5 bg-emerald-500" />
        </span>
        <span className="font-mono tracking-[0.14em] uppercase">Active</span>
      </span>
    );
  }
  const isWip = tone === "wip";
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-[3px] border ${isWip ? "border-amber-500/40 bg-amber-500/10 text-amber-700" : "border-nex-navy/25 bg-nex-navy/5 text-nex-navy/65"}`}
      data-testid="nds-cap-status-standby"
      style={{ fontSize: "0.55rem" }}
    >
      <Circle className="w-1.5 h-1.5" fill="currentColor" strokeWidth={0} />
      <span className="font-mono tracking-[0.14em] uppercase">
        {isWip ? "In Build" : "Standby"}
      </span>
    </span>
  );
}
